// Change an event that comes from a layer, only in this calendar (an override rule).
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Field, FormError } from '@/components/app/common';
import { createStore } from '@/lib/store';
import { post } from '@/lib/api';
import { addDays } from '@/lib/format';
import { notify } from '@/lib/notify';
import type { PreviewItem, Status } from '@/lib/types';
import { StatusPicker } from './EventDialog';
import { TimeEditor, timeFromEvent, timeValid, type TimeValue } from './TimeEditor';

interface State {
  feedId: string;
  feedName: string;
  feedTz: string;
  item: PreviewItem;
  onDone: () => void;
}
const store = createStore<State | null>(null);
export const openOverrideDialog = (s: State) => store.set(s);

export function OverrideDialog() {
  const s = store.use();
  return <Dialog open={!!s} onOpenChange={(o) => !o && store.set(null)}>{s ? <OverrideForm key={s.item.key} s={s} /> : null}</Dialog>;
}

function OverrideForm({ s }: { s: State }) {
  const e = s.item.event;
  const existing = s.item.rule?.action === 'override';
  const [f, setF] = useState({ title: e.title, location: e.location, description: e.description, status: e.status as Status, note: s.item.rule?.note ?? '' });
  const [changeTime, setChangeTime] = useState(false);
  const [time, setTime] = useState<TimeValue>(() => timeFromEvent(e, s.feedTz));
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const patch: Record<string, unknown> = {};
    for (const k of ['title', 'location', 'description', 'status'] as const) if ((f[k] ?? '') !== ((e as unknown as Record<string, string>)[k] ?? '')) patch[k] = f[k];
    if (changeTime) {
      if (!timeValid(time)) return setErr('Please check the start and end.');
      patch.all_day = time.all_day;
      patch.start = time.start;
      patch.end = time.all_day ? addDays(time.end, 1) : time.end;
      if (!time.all_day) patch.timezone = time.timezone;
    }
    if (!Object.keys(patch).length) return setErr('You haven’t changed anything yet.');
    setBusy(true);
    try {
      await post(`/feeds/${s.feedId}/rules`, { target_key: s.item.key, action: 'override', patch, note: f.note });
      notify('Change saved');
      store.set(null);
      s.onDone();
    } catch (ex) {
      setErr((ex as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogContent className="max-h-[calc(100svh-2rem)] overflow-y-auto sm:max-w-2xl">
      <form className="grid grid-cols-[minmax(0,1fr)] gap-5" onSubmit={submit} onInput={() => setErr('')}>
        <DialogHeader>
          <DialogTitle>Change “{e.title}”</DialogTitle>
          <DialogDescription>
            Your change applies to “{s.feedName}” and calendars that include it. Anything you leave alone keeps syncing from “{s.item.origin_name}”.
          </DialogDescription>
        </DialogHeader>
        <Field label="Title" htmlFor="ov-title">
          <Input id="ov-title" name="title" value={f.title} onChange={(x) => setF({ ...f, title: x.target.value })} />
        </Field>
        <Field label="Location" htmlFor="ov-loc">
          <Input id="ov-loc" name="location" value={f.location} onChange={(x) => setF({ ...f, location: x.target.value })} />
        </Field>
        <Field label="Notes" htmlFor="ov-notes">
          <Textarea id="ov-notes" name="description" rows={3} value={f.description} onChange={(x) => setF({ ...f, description: x.target.value })} />
        </Field>
        <Field label="Status">
          <StatusPicker value={f.status} onChange={(st) => setF({ ...f, status: st })} />
        </Field>
        <div className="flex items-center gap-2">
          <Switch id="ov-time" checked={changeTime} onCheckedChange={setChangeTime} />
          <Label htmlFor="ov-time">Change the date or time</Label>
        </div>
        {changeTime ? <TimeEditor value={time} onChange={setTime} feedTz={s.feedTz} idPrefix="ov" /> : null}
        <Field label="Private note" htmlFor="ov-note" hint="Only people who can edit this calendar see it.">
          <Input id="ov-note" name="note" maxLength={200} placeholder="e.g. Our class goes to the afternoon session" value={f.note} onChange={(x) => setF({ ...f, note: x.target.value })} />
        </Field>
        <FormError>{err}</FormError>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => store.set(null)}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : existing ? 'Update change' : 'Save change'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
